import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

// Universal Components
import UniversalHeader from "./components/UniversalHeader/UniversalHeader";
import UniversalSidebar from "./components/UniversalSidebar/UniversalSidebar";
import MobileFooter from "./Views/MobileFooter";
import DesktopFooter from "./Views/DesktopFooter";

// Auth
import AuthProvider from "./auth/AuthProvider";
import AuthPage from "./auth/AuthPage";
import Signup from "./auth/Signup";
import Login from "./auth/Login";

// Pages
import FirestoreTest from "./components/FirestoreTest";
import News from "./Views/News";
import ArticleDetailPage from "./Views/ArticleDetailPage";

// Layouts (simplified - just content, no headers/sidebars)
import GameLayout from "./components/Games/GameLayout";
import AnotherOnePiece from "./Views/AnotherOnePiece";
import GameHistoryOverview from "./components/Games/GameHistoryOverview";
import GameHistory from "./components/Games/GameHistory";

import BlogLayout from "./components/Blog/BlogLayout";
import BlogHome from "./components/Blog/BlogHome";
import BlogDetail from "./components/Blog/BlogDetail/BlogDetail";

import MangaLayout from "./components/Manga/MangaLayout";
import MangaHomePage from "./components/Manga/MangaHomePage";
import MangaDetailPage from "./components/Manga/MangaDetail/MangaDetailPage";
import MangaNotFoundPage from "./components/Manga/MangaNotFoundPage";

import CommunityLayout from "./components/Community/CommunityLayout";
import CommunityPage from "./Views/communityplatform/CommunityPage";
import CommunityMember from "./components/Community/Communitymember";
import CommunityHomePage from "./Views/communityplatform/CommunityHomePage";
import CreateRoomPage from "./Views/communityplatform/CreateRoomPage";
import MyRoomsPage from "./Views/communityplatform/MyRoomsPage";
import CommunityLists from "./components/Community/CommunityLists";
import CommunityFavourites from "./components/Community/CommunityFavourites";

import TheNewsLayout from "./components/NewsFolder/NewsLayout";
import TheNewsHomePage from "./components/NewsFolder/NewsHomePage";
import NewsDetailPage from "./components/NewsFolder/NewsDetailPage";

import WebLayout from "./components/NewWebFolder/WebLayout";
import WebHomePage from "./components/NewWebFolder/WebHomePage";

import ProfileLayout from "./components/Profile/ProfileLayout";
import ProfilePage from "./components/Profile/ProfilePage";
import ProfileDetails from "./components/Profile/ProfileDetails";
import ProfileHeader from "./components/Profile/ProfileHeader";

import AnimeHubLayout from "./components/AnimeHub/AnimeHubLayout";
import AnimeHubHome from "./components/AnimeHub/AnimeHubHome";
import TopAiring from "./components/AnimeHub/Pages/TopAiring";
import TopAnime from "./components/AnimeHub/Pages/TopAnime";
import TopUpcoming from "./components/AnimeHub/Pages/TopUpcoming";
import MostPopular from "./components/AnimeHub/Pages/MostPopular";
import MostFavorited from "./components/AnimeHub/Pages/MostFavorited";
import AnimeCharacters from "./components/AnimeHub/Pages/AnimeCharacters";
import Search from "./components/Search/Search";
import AllAnimes from "./components/AnimeHub/Pages/AllAnimes";
import AnimeDetailPage from "./components/AnimeHub/Pages/AnimeDetailPage";

import RouteTracker from "./components/Analytics/RouteTracker";
import { initGA } from "./utils/analytics";

import "font-awesome/css/font-awesome.min.css";
import "./App.css";

// Initialize Google Analytics
initGA();

const App = () => {
  const isMobile = window.innerWidth <= 768;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  return (
    <AuthProvider>
      <Router>
        <div className="app-container">
          {/* Universal Header - Always at top */}
          <UniversalHeader 
            onToggleSidebar={toggleSidebar} 
            isSidebarOpen={isSidebarOpen}
          />

          {/* Universal Sidebar - Left side */}
          <UniversalSidebar 
            isOpen={isSidebarOpen} 
            onClose={closeSidebar}
          />

          {/* Analytics Route Tracker — tracks every page change */}
          <RouteTracker />

          {/* Main Content Area */}
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Navigate to="/games" replace />} />

  {/* Games Routes */}
  <Route path="/games" element={<GameLayout />}>
                <Route path="another-one-piece" element={<AnotherOnePiece animeIds={[21]} theme="onePiece" />} />
                <Route path="naruto" element={<AnotherOnePiece animeIds={[20]} theme="naruto" />} />
                <Route path="bleach" element={<AnotherOnePiece animeIds={[269]} theme="bleach" />} />
                <Route path="one-punch" element={<AnotherOnePiece animeIds={[30276]} theme="onePunch" />} />
                <Route path="dragon-ball" element={<AnotherOnePiece animeIds={[223]} theme="dragonBall" />} />
              </Route>

              <Route path="/game-history" element={<GameLayout />}>
                <Route index element={<GameHistoryOverview />} />
                <Route path=":theme" element={<GameHistory userId="guest_user" />} />
              </Route>

              {/* Auth Routes */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/signup" element={<Signup />} />
              <Route path="/auth/login" element={<Login />} />

              {/* Blog Routes */}
              <Route path="/new-blog-home" element={<BlogLayout />}>
                <Route index element={<BlogHome />} />
                <Route path="article/:id" element={<BlogDetail />} />
              </Route>

              {/* Manga Routes */}
              <Route path="/manga-home" element={<MangaLayout />}>
                <Route index element={<MangaHomePage />} />
                <Route path="manga/:mangaId" element={<MangaDetailPage />} />
              </Route>

              {/* Community Routes */}
              <Route path="/community" element={<CommunityLayout />}>
                <Route index element={<CommunityHomePage />} />
                <Route path="page" element={<CommunityPage />} />
                <Route path="member" element={<CommunityMember />} />
                <Route path="create-room" element={<CreateRoomPage />} />
                <Route path="my-rooms" element={<MyRoomsPage />} />
                <Route path="lists" element={<CommunityLists />} />
                <Route path="favourites" element={<CommunityFavourites />} />
              </Route>

              {/* News Routes */}
              <Route path="/the-news" element={<TheNewsLayout />}>
                <Route index element={<TheNewsHomePage />} />
                <Route path=":url" element={<NewsDetailPage />} />
                <Route path=":id" element={<NewsDetailPage />} />
              </Route>

              {/* New Website Routes */}
              <Route path="/new-home" element={<WebLayout />}>
                <Route index element={<WebHomePage />} />
              </Route>

              {/* Profile Routes */}
              <Route path="/profile" element={<ProfileLayout />}>
                <Route index element={<ProfilePage />} />
                <Route path="details" element={<ProfileDetails />} />
                <Route path="header" element={<ProfileHeader />} />
              </Route>

              {/* AnimeHub Routes */}
              <Route path="/animehub" element={<AnimeHubLayout />}>
                <Route index element={<AnimeHubHome />} />
                <Route path="top-airing" element={<TopAiring />} />
                <Route path="top-anime" element={<TopAnime />} />
                <Route path="top-upcoming" element={<TopUpcoming />} />
                <Route path="most-popular" element={<MostPopular />} />
                <Route path="most-favorited" element={<MostFavorited />} />
                <Route path="characters" element={<AnimeCharacters />} />
                <Route path="search" element={<Search />} />
                <Route path="all-animes" element={<AllAnimes />} />
                <Route path="anime/:id" element={<AnimeDetailPage />} />
              </Route>

              {/* Legacy News Routes */}
              <Route path="/News" element={<News />} />
              <Route path="/news/:id" element={<ArticleDetailPage />} />
              
              {/* Firestore Test */}
              <Route path="/firestore-test" element={<FirestoreTest />} />

              {/* 404 */}
              <Route path="*" element={<MangaNotFoundPage />} />
            </Routes>
          </main>

          {/* Footer */}
          {isMobile ? <MobileFooter /> : <DesktopFooter />}
        </div>
      </Router>
    </AuthProvider>
  );
};

export default App;
